import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, BookOpen, BriefcaseBusiness } from 'lucide-react';
import CaseStudiesLayout from './CaseStudiesLayout';
import CaseStudyCard from './CaseStudyCard';
import CaseStudyLanguageLabel from './CaseStudyLanguageLabel';
import {
  CASE_STUDY_LOCALES,
  caseStudyLocaleNames,
  caseStudyUi,
  getCaseStudyPath,
  getLocalizedCaseStudies,
  getPreferredCaseStudyLocale,
  isSupportedCaseStudyLocale,
  DEFAULT_CASE_STUDY_LOCALE,
  setCaseStudyLocaleOverride,
} from '../../data/caseStudies';
import { scrollToTop } from '../../utils/scrollUtils';

const CaseStudiesLanguageSwitcher = ({ locale, navigate }) => (
  <div className="case-studies-language">
    <div className={`case-study-language-options active-${locale}`}>
      {CASE_STUDY_LOCALES.map((targetLocale) => {
        const isActive = targetLocale === locale;

        return (
          <button
            key={targetLocale}
            type="button"
            className={`case-study-language-option ${isActive ? 'active' : ''}`}
            aria-pressed={isActive}
            title={caseStudyLocaleNames[targetLocale]}
            onClick={() => {
              if (!isActive) {
                setCaseStudyLocaleOverride(targetLocale);
                navigate(getCaseStudyPath(targetLocale), { preventScrollReset: true });
              }
            }}
          >
            <CaseStudyLanguageLabel locale={targetLocale} />
          </button>
        );
      })}
    </div>
  </div>
);

const CaseStudiesIndex = ({ isMenuOpen, setIsMenuOpen }) => {
  const { locale = DEFAULT_CASE_STUDY_LOCALE } = useParams();
  const navigate = useNavigate();
  const isSupportedLocale = isSupportedCaseStudyLocale(locale);
  const activeLocale = isSupportedLocale ? locale : DEFAULT_CASE_STUDY_LOCALE;
  const ui = caseStudyUi[activeLocale];
  const studies = getLocalizedCaseStudies(activeLocale);

  useEffect(() => {
    const preferredLocale = getPreferredCaseStudyLocale();

    if (!isSupportedLocale || activeLocale !== preferredLocale) {
      navigate(getCaseStudyPath(preferredLocale), {
        replace: !isSupportedLocale,
        preventScrollReset: true,
      });
    }
  }, [activeLocale, isSupportedLocale, navigate]);

  useEffect(() => {
    scrollToTop('auto');
  }, []);

  return (
    <CaseStudiesLayout
      title={ui.indexTitle}
      description={ui.indexDescription}
      locale={activeLocale}
      isMenuOpen={isMenuOpen}
      setIsMenuOpen={setIsMenuOpen}
    >
      <section className="case-studies-hero">
        <div className="case-study-bg-glow" aria-hidden="true" />
        <div className="case-studies-container">
          <div className="case-studies-hero-inner">
            <h1 className="case-studies-title">{ui.indexTitle}</h1>
            <p className="case-studies-subtitle">{ui.indexDescription}</p>
            <CaseStudiesLanguageSwitcher locale={activeLocale} navigate={navigate} />
          </div>
        </div>
      </section>

      <section className="case-studies-section">
        <div className="case-studies-container">
          {studies.length > 0 ? (
            <div className="case-study-grid">
              {studies.map((study) => (
                <CaseStudyCard key={study.slug} study={study} ui={ui} />
              ))}
            </div>
          ) : (
            <div className="case-study-empty">
              <div className="case-study-empty-icon">
                <BriefcaseBusiness className="h-8 w-8" />
              </div>
              <h2>{ui.emptyTitle}</h2>
              {ui.emptyDescription && <p>{ui.emptyDescription}</p>}
              <div className="case-study-empty-actions">
                <Link to="/contact" className="premium-btn inline-flex items-center justify-center">
                  {ui.contactCta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link to="/blog" className="secondary-btn inline-flex items-center justify-center">
                  <BookOpen className="mr-2 h-5 w-5" />
                  {ui.blogCta}
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </CaseStudiesLayout>
  );
};

export default CaseStudiesIndex;
